import React, { useEffect, useState } from "react";
import { API_BASE } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import { Activity, ChevronRight, ChevronDown } from "lucide-react";

const SUBJECT_LABELS = {
  polity: "Polity",
  history: "History",
  geography: "Geography",
  economy: "Economy",
  environment: "Environment",
  scitech: "Science & Tech",
  international_relations: "International Relations",
  society_security: "Society & Security",
  unknown: "General",
};

const BAND_STYLES = {
  strong: "bg-emerald-500 text-white border-emerald-600",
  moderate: "bg-amber-400 text-amber-950 border-amber-500",
  weak: "bg-rose-500 text-white border-rose-600",
  untouched: "bg-slate-200 text-slate-500 border-slate-300",
};

export const TopicHeatmap = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [aptitude, setAptitude] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };
        const [gsRes, aptRes] = await Promise.all([
          fetch(`${API_BASE}/chapter-practice/heatmap`, { headers }),
          fetch(`${API_BASE}/csat/aptitude-heatmap`, { headers }).catch(() => null),
        ]);
        if (gsRes.ok) setData(await gsRes.json());
        if (aptRes && aptRes.ok) setAptitude(await aptRes.json());
      } catch (e) {
        /* silent */
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <Card className="border-slate-200" data-testid="topic-heatmap-loading">
        <CardContent className="p-6 animate-pulse">
          <div className="h-4 w-40 bg-slate-200 rounded mb-4" />
          <div className="flex gap-2 flex-wrap">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="h-8 w-24 bg-slate-100 rounded-lg" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const subjects = data?.subjects || [];
  const aptSections = (aptitude?.sections || []).filter((s) => s.attempted > 0);
  const hasData = subjects.some((s) => s.total_attempted > 0) || aptSections.length > 0;

  // Collapsed-preview summary metrics
  const gsChapters = subjects.flatMap((s) => s.chapters || []);
  const aptChapters = aptSections.flatMap((s) => s.chapters || []);
  const allChapters = [...gsChapters, ...aptChapters];
  const weakCount = allChapters.filter((c) => c.band === "weak").length;
  const strongCount = allChapters.filter((c) => c.band === "strong").length;
  const totalAttempted =
    subjects.reduce((a, s) => a + (s.total_attempted || 0), 0) +
    (aptitude?.total_attempted || 0);

  return (
    <Card className="border-slate-200" data-testid="topic-heatmap">
      <CardHeader className="pb-3">
        <button
          type="button"
          onClick={() => hasData && setOpen((v) => !v)}
          className={`w-full flex items-center gap-2 text-left ${hasData ? "cursor-pointer" : "cursor-default"}`}
          data-testid="topic-heatmap-toggle"
          aria-expanded={open}
        >
          <Activity className="w-5 h-5 text-indigo-600 shrink-0" />
          <CardTitle className="text-lg text-slate-800 flex-1">
            Accuracy Map
            <span className="text-xs font-normal text-slate-400 ml-2">your accuracy by chapter</span>
          </CardTitle>
          {hasData && (
            <ChevronDown
              className={`w-5 h-5 text-slate-400 shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
            />
          )}
        </button>
      </CardHeader>
      <CardContent>
        {!hasData ? (
          <button
            onClick={() => navigate("/prelims/chapter-practice")}
            className="w-full text-left p-4 rounded-xl bg-indigo-50 hover:bg-indigo-100 transition-colors flex items-center justify-between"
            data-testid="heatmap-empty-cta"
          >
            <span className="text-sm text-slate-700">
              Practice a few chapters to unlock your personalised heatmap.
            </span>
            <span className="flex items-center gap-1 text-indigo-600 font-medium text-sm whitespace-nowrap">
              Start Practice <ChevronRight className="w-4 h-4" />
            </span>
          </button>
        ) : !open ? (
          /* Collapsed preview — click to expand full breakdown */
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="w-full text-left p-4 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors flex items-center justify-between gap-3"
            data-testid="heatmap-collapsed-preview"
          >
            <div className="flex items-center gap-2 flex-wrap text-xs">
              <span className="px-2 py-1 rounded-lg bg-white border border-slate-200 text-slate-600 font-medium">
                {allChapters.length} topics tracked
              </span>
              {weakCount > 0 && (
                <span className="px-2 py-1 rounded-lg bg-rose-100 text-rose-700 font-medium">
                  {weakCount} weak {weakCount === 1 ? "area" : "areas"}
                </span>
              )}
              {strongCount > 0 && (
                <span className="px-2 py-1 rounded-lg bg-emerald-100 text-emerald-700 font-medium">
                  {strongCount} strong
                </span>
              )}
              <span className="text-slate-400">· {totalAttempted} attempted</span>
            </div>
            <span className="flex items-center gap-1 text-indigo-600 font-medium text-sm whitespace-nowrap">
              View <ChevronDown className="w-4 h-4" />
            </span>
          </button>
        ) : (
          <>
            {/* Legend */}
            <div className="flex items-center gap-3 mb-4 text-xs text-slate-500 flex-wrap">
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-500 inline-block" /> Strong ≥75%</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-400 inline-block" /> Moderate 50-74%</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-rose-500 inline-block" /> Weak &lt;50%</span>
            </div>
            <div className="space-y-4">
              {subjects
                .filter((s) => s.total_attempted > 0)
                .map((s) => (
                  <div key={s.subject} data-testid={`heatmap-subject-${s.subject}`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="font-semibold text-sm text-slate-700">
                        {SUBJECT_LABELS[s.subject] || s.subject}
                      </span>
                      <span className="text-xs text-slate-500">
                        {s.accuracy}% · {s.total_attempted} attempted
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {s.chapters.map((c) => (
                        <button
                          key={c.chapter}
                          onClick={() => navigate("/prelims/chapter-practice")}
                          title={`${c.chapter} — ${c.accuracy}% (${c.correct}/${c.attempted})`}
                          className={`px-2.5 py-1 rounded-lg border text-xs font-medium transition-transform hover:scale-105 ${BAND_STYLES[c.band]}`}
                          data-testid={`heatmap-chapter-${s.subject}-${c.chapter.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                          {c.chapter} · {c.accuracy}%
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
            </div>

            {/* Aptitude (CSAT / SI / SSC) — section + chapter-level strengths */}
            {aptSections.length > 0 && (
              <div className="mt-5 pt-4 border-t border-slate-100" data-testid="heatmap-aptitude">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-sm text-slate-700">
                    Aptitude <span className="text-xs font-normal text-slate-400">CSAT · SI · SSC</span>
                  </span>
                  {aptitude?.total_attempted > 0 && (
                    <span className="text-xs text-slate-500">
                      {aptitude.accuracy}% · {aptitude.total_attempted} attempted
                    </span>
                  )}
                </div>
                <div className="space-y-3">
                  {aptSections.map((s) => (
                    <div key={s.section} data-testid={`heatmap-aptitude-section-${s.section}`}>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="font-medium text-xs text-slate-600 flex items-center gap-1.5">
                          <span className={`w-2 h-2 rounded-full inline-block ${
                            s.band === "strong" ? "bg-emerald-500" : s.band === "moderate" ? "bg-amber-400" : "bg-rose-500"
                          }`} />
                          {s.label}
                        </span>
                        <span className="text-[11px] text-slate-400">
                          {s.accuracy}% · {s.attempted} attempted
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {(s.chapters && s.chapters.length > 0) ? (
                          s.chapters.map((c) => (
                            <button
                              key={c.chapter}
                              onClick={() => navigate("/csat-suite")}
                              title={`${c.chapter} — ${c.accuracy}% (${c.correct}/${c.attempted})`}
                              className={`px-2.5 py-1 rounded-lg border text-xs font-medium transition-transform hover:scale-105 ${BAND_STYLES[c.band]}`}
                              data-testid={`heatmap-aptitude-chapter-${s.section}-${c.chapter.toLowerCase().replace(/\s+/g, "-")}`}
                            >
                              {c.chapter} · {c.accuracy}%
                            </button>
                          ))
                        ) : (
                          <button
                            onClick={() => navigate("/csat-suite")}
                            title={`${s.label} — ${s.accuracy}% (${s.correct}/${s.attempted})`}
                            className={`px-2.5 py-1 rounded-lg border text-xs font-medium transition-transform hover:scale-105 ${BAND_STYLES[s.band]}`}
                            data-testid={`heatmap-aptitude-${s.section}`}
                          >
                            {s.label} · {s.accuracy}%
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default TopicHeatmap;
